/* eslint-disable class-methods-use-this, no-return-assign */
import Promise from 'bluebird';
import { PlayerTech } from '../../constants';
import PlayerBase from '../PlayerBase';

class BrightcovePlayer extends PlayerBase {
    constructor({ playerTech, ...options } = {}) {
        super();
        this._playerTech = playerTech;
        this._options = options;
    }

    get tech() {
        return PlayerTech.BRIGHTCOVE;
    }

    get container() {
        return this._player && this._player.bundle.element.parentNode;
    }

    get player() {
        return this._player && this._player.player;
    }

    get analytics() {
        return this._player && this._player.analytics;
    }

    get advertising() {
        return this._player && this._player.advertising;
    }

    currentTime(value) {
        return new Promise((resolve, reject) => {
            try {
                if (value && this._player) {
                    this._player.currentTime(value);
                    resolve(this);
                    return;
                }
                resolve(this._player && this._player.currentTime());
            } catch (err) {
                reject(err);
            }
        });
    }

    createPlayer() {
        const ESPNVideoPlayer = window.ESPNVideoPlayer;
        return (
            this._playerPromise ||
            (this._playerPromise = new Promise((resolve, reject) => {
                if (ESPNVideoPlayer) {
                    this._player = new ESPNVideoPlayer(this._playerTech, this._options);
                    this._player.ready(resolve);
                } else {
                    setTimeout(() => {
                        reject(new Error('ESPN web player bunlde JS not available.'));
                    }, 1000);
                }
            }))
        );
    }

    addPlayerListeners() {
        const { player } = this._player || {};
        if (player) {
            this._unsubscribes = Object.keys(BrightcovePlayer.Events).map(key => {
                const callback = (...args) => {
                    this.emit(BrightcovePlayer.Events[key], ...args);
                };
                player.on(BrightcovePlayer.Events[key], callback);
                return () => {
                    player.off(BrightcovePlayer.Events[key], callback);
                };
            });
        }
    }

    removePlayerListeners() {
        if (this._unsubscribes) {
            while (this._unsubscribes.length) {
                this._unsubscribes.pop()();
            }
            this._unsubscribes = null;
        }
    }

    create(options) {
        return this.createPlayer().then(() => {
            const { guid, el, options: playOptions } = options;
            return new Promise(resolve => {
                this._player.embed(guid, el, playOptions, () => {
                    this.addPlayerListeners();
                    resolve(this);
                });
            });
        });
    }

    delete() {
        this.removePlayerListeners();
        this._player.unembed();
        return Promise.resolve(this);
    }

    play() {
        return new Promise((resolve, reject) => {
            try {
                this._player.play();
                resolve(this);
            } catch (err) {
                reject(err);
            }
        });
    }

    pause() {
        return new Promise((resolve, reject) => {
            try {
                this._player.pause();
                resolve(this);
            } catch (err) {
                reject(err);
            }
        });
    }

    mute() {
        return new Promise((resolve, reject) => {
            try {
                this._player.muted(true);
                resolve(this);
            } catch (err) {
                reject(err);
            }
        });
    }

    unmute() {
        return new Promise((resolve, reject) => {
            try {
                this._player.muted(false);
                resolve(this);
            } catch (err) {
                reject(err);
            }
        });
    }

    requestFullscreen() {
        return new Promise((resolve, reject) => {
            try {
                this._player.requestFullscreen();
                resolve(this);
            } catch (err) {
                reject(err);
            }
        });
    }

    exitFullscreen() {
        return new Promise((resolve, reject) => {
            try {
                this._player.exitFullscreen();
                resolve(this);
            } catch (err) {
                reject(err);
            }
        });
    }

    error(err, content) {
        return new Promise((resolve, reject) => {
            try {
                this._player.error(err, content);
                resolve(this);
            } catch (err2) {
                reject(err2);
            }
        });
    }

    getPlayerConfigs() {
        return Promise.resolve(this._player.bundle.options);
    }

    updatePlayerConfigs(newConfigs) {
        return Promise.resolve(newConfigs);
    }
}

BrightcovePlayer.Events = {
    ESPN_PLAYING: 'espn.playing',
    ESPN_PLAY: 'espn.play',
    ESPN_PAUSE: 'espn.pause',
    ESPN_AD_POD_STARTED: 'espn.ads-pod-started',
    ESPN_AD_POD_ENDED: 'espn.ads-pod-ended',
    ESPN_AD_STARTED: 'espn.ads-ad-started',
    ESPN_AD_ENDED: 'espn.ads-ad-ended',
    ESPN_CONTENT_ENDED: 'espn.contentended',
    ESPN_CONTENT_LOADED: 'espn.contentloaded',
    ESPN_PROGRAM_CHANGE: 'espn.programchange',
    ESPN_AUTOPLAY_TOGGLED: 'espn.autoplaytoggled',
    ESPN_SOCIAL_OPENED: 'socialopened',
    ESPN_SOCIAL_CLOSED: 'socialclosed',
    ESPN_CAPTION_OPENED: 'captionopened',
    ESPN_CAPTION_CLOSED: 'captionclosed',
    ESPN_ERROR: 'espn.error',
    ESPN_DRM_ERROR: 'espn.drm.error',
    ESPN_ADS_AD_SKIPPED: 'ads-ad-skipped'
};

export default BrightcovePlayer;



// WEBPACK FOOTER //
// ./app/webapp/common/video-player/player-tech/BrightcovePlayer/index.js